import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, Card, CardContent, CardHeader, Grid, Paper, Stack } from '@mui/material';
import { FormSelectOption } from 'components/forms/FormSelect';
import { AccountNumberParams } from 'pages/managePricing/managePricingResolver';
import { useForm } from 'react-hook-form';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import { ROUTES } from 'routing';
import { ApprovalStatusEnum } from 'shared/constants';
import { useGetEndUserDropdown } from 'shared/hooks';
import UseDynamicUrl from 'shared/hooks/useDynamicUrl';
import {
    determineEnduserDisplay,
    formatFourDecimalMoney,
    formatLocalDateToLocalISODateTimeString,
    oneYearAheadToCurrentDate,
} from 'shared/utility';
import { AddDeviationsData, EndUserDropdown } from 'types/backend';
import { AddModelCurrentPricesForm } from 'types/forms';
import AddDeviationForm from './AddDeviationForm';
import { ConfirmDeviations } from './confirmDeviations';

type SetupDeviationsProps = {
    selectedProducts: AddDeviationsData[];
    customerName: string;
};

export default function SetupDeviations({ selectedProducts, customerName }: SetupDeviationsProps) {
    const params = useParams<AccountNumberParams>();
    const {
        handleSubmit,
        control,
        formState: { errors },
        getValues,
        watch,
        setValue,
    } = useForm<AddModelCurrentPricesForm>({
        defaultValues: {
            addedDeviations: selectedProducts.map((selectedProduct) => ({
                modelCurrentPrice: {
                    currentpriceid: '0',
                    recid: '0',
                    accountnumber: params.accountNumber,
                    enduseraccountnumber: '',
                    pricetype: 'Deviation',
                    itemorgroup: selectedProduct.productId,
                    startdate: formatLocalDateToLocalISODateTimeString(new Date().toLocaleDateString()),
                    enddate: oneYearAheadToCurrentDate(),
                    price: '',
                    status: ApprovalStatusEnum.NEEDSAPPROVAL,
                    approver: '', //logic for approver contained  in confirmDeviations.tsx
                    estmonthlyrevenue: '',
                    competitor: '',
                    competitorprice: '',
                    source: '', // Question: AX?
                    lastupdated: '',
                    modifiedby: '',
                    effectivestart: '',
                    effectiveend: '',
                    deleted: '0',
                },
                deviationDetails: {
                    deviationReason: '',
                    deviationType: '',
                },
                selectedProduct,
                isOneTimeOnly: false,
                endUserDisplay: '',
                street: '',
                city: '',
                zipCode: '',
                state: '',
            })),
        },
    });

    const endUserDropdownResponse = useGetEndUserDropdown(params.accountNumber);
    const isEndUserDropdownLoading = endUserDropdownResponse.isLoading || endUserDropdownResponse.isFetching;

    const endUserDropdownOptions: FormSelectOption<EndUserDropdown>[] =
        endUserDropdownResponse.data?.rows?.map((endUser) => {
            return {
                value: endUser.endusernumber,
                displayText: determineEnduserDisplay(
                    endUser.city,
                    endUser.state,
                    endUser.street,
                    endUser.endusername,
                    endUser.endusernumber
                ),
                original: endUser,
            };
        }) || [];

    const backHandler = UseDynamicUrl<AccountNumberParams>({ route: ROUTES.addDeviationsCatalog });
    const continueHandler = UseDynamicUrl<AccountNumberParams>({ route: ROUTES.addDeviationsConfirm });

    const continueClickHandler = handleSubmit((submission: AddModelCurrentPricesForm) => {
        continueHandler.clickHandler();
    });

    if (selectedProducts.length <= 0) {
        return <Redirect to={backHandler.path} />;
    }

    return (
        <Switch>
            <Route path={ROUTES.addDeviationsConfirm}>
                <ConfirmDeviations form={getValues()} customerName={customerName} />
            </Route>
            <Route path={ROUTES.addDeviationsSetup}>
                <Stack sx={{ gap: 2 }}>
                    <Paper sx={{ padding: '1rem 2rem', maxHeight: '70vh', overflowY: 'scroll' }} elevation={0}>
                        <Stack sx={{ gap: 2 }}>
                            {selectedProducts.map((selectedProduct, index) => (
                                <Card key={selectedProduct.id} sx={{ border: '1px solid gray' }}>
                                    <CardHeader
                                        sx={{ backgroundColor: '#f1f1f1', borderBottom: '1px solid gray' }}
                                        title={selectedProduct.productId}
                                    />
                                    <CardContent>
                                        <Grid container spacing={2} sx={{ padding: '1rem 0' }}>
                                            <Grid
                                                container
                                                item
                                                sx={{ backgroundColor: '#f1f1f1', padding: '1rem 2rem' }}
                                            >
                                                <Grid item xs={4}>
                                                    Current Market Price:{' '}
                                                    <strong>{formatFourDecimalMoney(selectedProduct.price)}</strong>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    Tier 1 Target Price:{' '}
                                                    <strong>
                                                        {formatFourDecimalMoney(selectedProduct.tierOnePrice)}
                                                    </strong>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    Customer Tier Target:{' '}
                                                    <strong>
                                                        {formatFourDecimalMoney(selectedProduct.targetPrice)}
                                                    </strong>
                                                </Grid>
                                            </Grid>
                                            <AddDeviationForm
                                                {...{
                                                    index,
                                                    control,
                                                    errors,
                                                    endUserDropdownOptions,
                                                    isEndUserDropdownLoading,
                                                    watch,
                                                    setValue,
                                                }}
                                            />
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </Paper>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            variant={'contained'}
                            sx={{
                                backgroundColor: 'secondary.main',
                                color: 'black',
                                '&:hover': {
                                    backgroundColor: 'secondary.light',
                                    color: 'black',
                                },
                            }}
                            onClick={backHandler.clickHandler}
                            startIcon={<ChevronLeft />}
                        >
                            Back
                        </Button>
                        <Button
                            variant={'contained'}
                            sx={{
                                backgroundColor: 'secondary.main',
                                color: 'black',
                                '&:hover': {
                                    backgroundColor: 'secondary.light',
                                    color: 'black',
                                },
                            }}
                            onClick={continueClickHandler}
                            endIcon={<ChevronRight />}
                        >
                            Continue
                        </Button>
                    </Box>
                </Stack>
            </Route>
        </Switch>
    );
}
